"use client";

import React, { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Zap, Atom, Box, Rocket, Sparkles, Compass, RotateCcw, Sliders, CircleCheck, Info } from 'lucide-react';
import { Reveal } from "@/components/cosmic/Reveal";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { useReducedMotion } from "@/components/cosmic/useReducedMotion";
import "@/components/cosmic/cosmic.css";
import styles from "../WhatIfUniverse.module.css";
export interface ScenarioDefinition {
  id: string;
  name: string;
  symbol: string;
  tagline: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  unit: string;
  sliderLabel: string;
  min: number;
  max: number;
  step: number;
  defaultVal: number;
  established: string;
  speculation: string;
  visualType: "star-orbit" | "atom-orbit" | "field-rings" | "hyper-cube" | "expansion-dots" | "electron-cloud";
  getReadout: (value: number) => {
    stateLabel: string;
    subLabel: string;
    summary: string;
    physicsConsequence: string;
    speculativeObservation: string;
    metricA: {
      label: string;
      val: string;
    };
    metricB: {
      label: string;
      val: string;
    };
    metricC: {
      label: string;
      val: string;
    };
  };
}
const SCENARIOS: ScenarioDefinition[] = [{
  id: "gravity",
  name: "Gravitational Constant",
  symbol: "G",
  tagline: "Stellar lifetimes & orbital curvature",
  icon: Compass,
  unit: "%",
  sliderLabel: "Gravitational Coupling Variation (ΔG / G₀)",
  min: -50,
  max: 50,
  step: 5,
  defaultVal: 0,
  established: "Stellar luminosity scales approximately with the fourth power of gravitational coupling (L ∝ G⁴ M³), meaning stellar lifespan scales as ~ G⁻³. A 20% increase in G shortens main-sequence stellar fusion tenfold, collapsing Earth-analogue habitability windows before complex biochemistry can establish.",
  speculation: "Life in a compressed high-G regime would require low-profile crystalline, distributed hydrostatic anatomy or liquid-phase substrates to survive crushing surface acceleration and brutal seismic tides.",
  visualType: "star-orbit",
  getReadout: (val: number) => {
    if (val === 0) {
      return {
        stateLabel: "Cosmic Equilibrium (Standard Reality)",
        subLabel: "Stable Goldilocks stellar lifecycle & orbital geometry",
        summary: "Stellar nucleosynthesis proceeds steadily; standard planetary orbits endure for billions of years.",
        physicsConsequence: "Inverse-square orbital equilibrium sustains liquid water zones; stars like Sol burn for ~10 billion years.",
        speculativeObservation: "Balanced planetary conditions permit multi-cellular cellular differentiation and geological stability.",
        metricA: {
          label: "Stellar Lifetime",
          val: "~10.0 Gyr"
        },
        metricB: {
          label: "Orbital Period",
          val: "1.00 yr"
        },
        metricC: {
          label: "Surface Gravity",
          val: "1.00 g"
        }
      };
    }
    if (val > 0) {
      const factor = 1 + val / 100;
      const life = (10 / Math.pow(factor, 3)).toFixed(1);
      const orb = (1 / Math.sqrt(factor)).toFixed(2);
      return {
        stateLabel: `Hyper-Gravitational Coupling (+${val}%)`,
        subLabel: val >= 30 ? "Severe stellar compression & orbital contraction" : "Accelerated stellar nucleosynthesis",
        summary: "Stars consume their core nuclear reserves at hyper-accelerated rates, collapsing into neutron stars or black holes.",
        physicsConsequence: `Stellar core pressures skyrocket; solar analogues burn through hydrogen in only ~${life} Gyr. Tidal shear contracts planetary orbits to ${orb} AU.`,
        speculativeObservation: "Biological organisms would need hyper-dense exoskeletons or flattened morphologies; surface mountains collapse under self-weight.",
        metricA: {
          label: "Stellar Lifetime",
          val: `~${life} Gyr`
        },
        metricB: {
          label: "Orbital Radius",
          val: `${orb} AU`
        },
        metricC: {
          label: "Tidal Stress",
          val: `+${(val * 2.2).toFixed(0)}%`
        }
      };
    }
    const factor = 1 + val / 100;
    const life = (10 / Math.pow(Math.max(0.2, factor), 3)).toFixed(1);
    return {
      stateLabel: `Hypo-Gravitational Diffusion (${val}%)`,
      subLabel: val <= -30 ? "Nebular ignition failure & planetary drift" : "Low core fusion & expanded stellar hulls",
      summary: "Molecular gas clouds struggle to collapse; stars burn as dim red smoldering orbs with weak gravitational wells.",
      physicsConsequence: "Jeans mass requirements for nebular collapse swell; smaller protostellar discs fail to achieve core hydrogen fusion.",
      speculativeObservation: "Planetary bodies retain faint, porous atmospheres; orbital drifting scatters planetary systems into interstellar voids.",
      metricA: {
        label: "Stellar Lifetime",
        val: `>${life} Gyr`
      },
      metricB: {
        label: "Escape Velocity",
        val: `${(factor * 11.2).toFixed(1)} km/s`
      },
      metricC: {
        label: "Core Density",
        val: `${(factor * 100).toFixed(0)}%`
      }
    };
  }
}, {
  id: "fine-structure",
  name: "Electromagnetic Strength",
  symbol: "α",
  tagline: "Fine-structure constant & chemistry",
  icon: Zap,
  unit: "%",
  sliderLabel: "Fine-Structure Constant Variation (Δα / α₀)",
  min: -50,
  max: 50,
  step: 5,
  defaultVal: 0,
  established: "The fine-structure constant (α ≈ 1/137.036) governs electromagnetic bonding strength. If α changed by merely ±4%, stellar carbon and oxygen production via the Hoyle triple-alpha resonance in red giants would fail completely, precluding chemical diversity.",
  speculation: "If molecular bonds were twice as rigid, crystalline biochemistry might compute neural thoughts at near light-speed, or biochemical metabolic circuits would freeze into immutable ceramic-like lattices.",
  visualType: "atom-orbit",
  getReadout: (val: number) => {
    if (val === 0) {
      return {
        stateLabel: "Hoyle State Resonance Active (Standard α)",
        subLabel: "Perfect carbon-oxygen nuclear synthetic balance",
        summary: "Electromagnetic repulsion and nuclear strong attraction perfectly balance, enabling organic chemistry.",
        physicsConsequence: "Red giant nucleosynthesis yields both carbon-12 and oxygen-16 in proportions essential for planets and water.",
        speculativeObservation: "Complex carbon backbones with flexible covalent bonding allow genetic coding and enzyme catalysts.",
        metricA: {
          label: "Bohr Radius",
          val: "0.529 Å"
        },
        metricB: {
          label: "Bond Energy",
          val: "1.00 E₀"
        },
        metricC: {
          label: "Chemical Variety",
          val: "Optimal"
        }
      };
    }
    if (val > 0) {
      return {
        stateLabel: `Strong Electromagnetic Binding (+${val}%)`,
        subLabel: "Tight atomic shells & suppressed proton fusion",
        summary: "Electrons orbit closer to nuclei with immense binding energies; Coulomb repulsion blocks stellar proton fusion.",
        physicsConsequence: "High Coulomb barriers suppress hydrogen fusion in stellar cores. Atoms become hyper-compact and rigid; chemistry turns brittle.",
        speculativeObservation: "Substances act with immense hardness; chemical reactions require extreme volcanic thermal activations to break bonds.",
        metricA: {
          label: "Bohr Radius",
          val: `${(0.529 / (1 + val / 100)).toFixed(3)} Å`
        },
        metricB: {
          label: "Coulomb Barrier",
          val: `+${(val * 1.8).toFixed(0)}%`
        },
        metricC: {
          label: "Chemical Reactivity",
          val: "Low"
        }
      };
    }
    return {
      stateLabel: `Weak Electromagnetic Binding (${val}%)`,
      subLabel: "Diffuse electron orbitals & chemical dissociation",
      summary: "Electrons are weakly bound to nuclei; thermal ambient noise strips outer valence electrons into cold ion plasmas.",
      physicsConsequence: "Bohr radii swell dramatically; molecular bonds dissolve at room temperature. Stars ignite easily but explode uncontrollably.",
      speculativeObservation: "Liquids fail to form surface tension; biological cell membranes cannot maintain molecular compartmentalization.",
      metricA: {
        label: "Bohr Radius",
        val: `${(0.529 / Math.max(0.1, 1 + val / 100)).toFixed(3)} Å`
      },
      metricB: {
        label: "Ionization Threshold",
        val: `${(100 + val).toFixed(0)}%`
      },
      metricC: {
        label: "Molecular Integrity",
        val: "Unstable"
      }
    };
  }
}, {
  id: "weak-force",
  name: "Weak Nuclear Force",
  symbol: "G_F",
  tagline: "Beta decay & supernova nucleosynthesis",
  icon: Atom,
  unit: "%",
  sliderLabel: "Weak Interaction Coupling (ΔG_F / G_F₀)",
  min: -50,
  max: 50,
  step: 5,
  defaultVal: 0,
  established: "The weak interaction mediates neutron-proton transitions (beta decay) and the p-p chain in stellar cores. A significantly weaker weak force would leave the primordial cosmos 100% helium, with zero hydrogen remaining for water or long-lived stars.",
  speculation: "In a 'Weakless Universe', fine-tuning of initial baryon-to-photon ratios could theoretically still allow stars, with exotic helium-burning chemistries and cold non-water solvents.",
  visualType: "field-rings",
  getReadout: (val: number) => {
    if (val === 0) {
      return {
        stateLabel: "Standard Electroweak Coupling (G_F)",
        subLabel: "Primordial 75% H / 25% He primordial inventory",
        summary: "Neutron freeze-out during Big Bang Nucleosynthesis yields abundant hydrogen fuel for stellar generations.",
        physicsConsequence: "Core solar p-p fusion proceeds at a steady, gentle rate regulated by weak proton-to-neutron conversions.",
        speculativeObservation: "Universal abundance of hydrogen enables ubiquitous water oceans and organic hydrocarbons.",
        metricA: {
          label: "Primordial Hydrogen",
          val: "~75%"
        },
        metricB: {
          label: "Neutron Half-life",
          val: "879.4 s"
        },
        metricC: {
          label: "Supernova Neutrino Blast",
          val: "Normal"
        }
      };
    }
    if (val > 0) {
      return {
        stateLabel: `Amplified Weak Interaction (+${val}%)`,
        subLabel: "Rapid neutron decay & pure hydrogen universe",
        summary: "Neutrons decay before primordial fusion begins, leaving virtually zero deuterium or primordial helium.",
        physicsConsequence: "First-generation stars lack catalytic isotopes; stellar ignition occurs at higher core thresholds with altered neutrino cooling.",
        speculativeObservation: "A pure-hydrogen cosmos has slower metal enrichment; heavy element metallicity takes more stellar generations to assemble.",
        metricA: {
          label: "Primordial Hydrogen",
          val: `${Math.min(99, 75 + val * 0.45).toFixed(0)}%`
        },
        metricB: {
          label: "Neutron Half-life",
          val: `${(879.4 / (1 + val / 100)).toFixed(0)} s`
        },
        metricC: {
          label: "Neutrino Cross-section",
          val: `+${val}%`
        }
      };
    }
    return {
      stateLabel: `Suppressed Weak Interaction (${val}%)`,
      subLabel: "Delayed neutron freeze-out & helium-dominated universe",
      summary: "Weak freeze-out occurs earlier; nearly all nucleons bind into helium-4 during the initial 3-minute synthesis.",
      physicsConsequence: "Hydrogen is nearly exhausted in primordial nucleosynthesis; stars must ignite directly through helium triple-alpha pathways.",
      speculativeObservation: "An oceanless helium universe where planetary atmospheres are dense noble gases without water or hydrocarbons.",
      metricA: {
        label: "Primordial Helium",
        val: `${Math.min(95, 25 - val * 1.1).toFixed(0)}%`
      },
      metricB: {
        label: "P-P Fusion Barrier",
        val: "High"
      },
      metricC: {
        label: "Supernova Ejection",
        val: "Weakened"
      }
    };
  }
}, {
  id: "spatial-dims",
  name: "Spatial Dimensionality",
  symbol: "D",
  tagline: "Topological stability & orbit calculus",
  icon: Box,
  unit: "D",
  sliderLabel: "Effective Spatial Dimensions (Continuous Metric)",
  min: -50,
  max: 50,
  step: 10,
  defaultVal: 0,
  established: "Ehrenfest's 1917 theorem demonstrates that stable closed planetary orbits and stable quantum atomic orbitals exist only in D = 3 spatial dimensions. In D ≥ 4, gravity follows an inverse-cube or higher law, making all orbits either spiral into collisions or escape indefinitely.",
  speculation: "Hypothetical 4D organisms could bypass 3D obstacles through hyper-spatial rotation, but their digestive tracts would split bodies apart unless topological biology operates on hyper-manifolds.",
  visualType: "hyper-cube",
  getReadout: (val: number) => {
    if (val === 0) {
      return {
        stateLabel: "D = 3 Spatial Dimensions (Our Reality)",
        subLabel: "Stable 1/r² force laws & closed Keplerian orbits",
        summary: "Poisson's equation produces stable elliptical orbits and localized quantum wavefunctions with discrete bound states.",
        physicsConsequence: "Planets execute closed orbits for billions of years without chaotic orbital disintegration.",
        speculativeObservation: "Volumetric three-dimensional space permits internal digestive, vascular, and neural networks without bisecting the organism.",
        metricA: {
          label: "Force Law",
          val: "1 / r²"
        },
        metricB: {
          label: "Orbital Stability",
          val: "Stable"
        },
        metricC: {
          label: "Atomic Bound States",
          val: "Discrete"
        }
      };
    }
    if (val > 0) {
      return {
        stateLabel: "D = 4+ Hyper-Spacetime (Higher Metric)",
        subLabel: "Inverse-cube 1/r³ potential & orbital instability",
        summary: "In four spatial dimensions, any minute perturbation sends orbiting bodies spiraling into the central star or flying into deep space.",
        physicsConsequence: "Gravitational potential scales as -1/r²; centrifugal barrier cannot stabilize against inward attraction. Hydrogen atoms have no stable ground state.",
        speculativeObservation: "Life cannot rely on stable planetary years; matter must exist as dynamically guided energy sheets or localized tesseract lattices.",
        metricA: {
          label: "Force Law",
          val: "1 / r³"
        },
        metricB: {
          label: "Orbital Stability",
          val: "Catastrophic"
        },
        metricC: {
          label: "Atomic Orbitals",
          val: "Self-collapsing"
        }
      };
    }
    return {
      stateLabel: "D = 2 Planar Topology (Flatland Metric)",
      subLabel: "Logarithmic potential & lack of local gravity waves",
      summary: "In two spatial dimensions, General Relativity has no propagating degrees of freedom (Weyl tensor vanishes in vacuum).",
      physicsConsequence: "Gravitational force falls off as 1/r (logarithmic potential); stars pull with infinite total energy at infinite radius.",
      speculativeObservation: "Any through-channel (like an alimentary canal or intersecting nerve) bisects a 2D entity into two separate halves.",
      metricA: {
        label: "Force Law",
        val: "1 / r"
      },
      metricB: {
        label: "Gravitational Waves",
        val: "Zero"
      },
      metricC: {
        label: "Topological Neural Wiring",
        val: "Non-intersecting"
      }
    };
  }
}, {
  id: "dark-energy",
  name: "Dark Energy Density",
  symbol: "Λ",
  tagline: "Cosmological constant & cosmic fate",
  icon: Rocket,
  unit: "%",
  sliderLabel: "Cosmological Constant Scaling (ΔΛ / Λ₀)",
  min: -50,
  max: 50,
  step: 5,
  defaultVal: 0,
  established: "The cosmological constant Λ accounts for ~68% of our universe's energy density. Steven Weinberg showed that if Λ were just 10 to 100 times larger, cosmic acceleration would have overpowered gravity before galaxies or galaxy clusters could ever collapse.",
  speculation: "A universe with negative Λ undergoes a Big Crunch; sentient civilizations in its final eons might compress computational time subjectively to experience infinite subjective thought within finite remaining seconds.",
  visualType: "expansion-dots",
  getReadout: (val: number) => {
    if (val === 0) {
      return {
        stateLabel: "Cosmic Coincidence Balance (Standard Λ)",
        subLabel: "Accelerated cosmic expansion began after galaxy assembly",
        summary: "Dark energy became dominant only around z ≈ 0.4 (~5 billion years ago), allowing primordial matter to condense into rich galaxy filaments.",
        physicsConsequence: "Structure formation completed before cosmic acceleration dispersed intergalactic gas.",
        speculativeObservation: "Our night sky is filled with billions of accessible galaxies, providing an observational window onto cosmic history.",
        metricA: {
          label: "Dark Energy Share",
          val: "~68.3%"
        },
        metricB: {
          label: "Hubble Constant",
          val: "~70 km/s/Mpc"
        },
        metricC: {
          label: "Structure Formation",
          val: "Completed"
        }
      };
    }
    if (val > 0) {
      return {
        stateLabel: `Elevated Dark Energy Acceleration (+${val}%)`,
        subLabel: "Premature cosmic dilution & isolated galaxies",
        summary: "Accelerated expansion sets in before cosmic structures can agglomerate into massive superclusters.",
        physicsConsequence: `Cosmic horizons shrink rapidly. Galaxies recede beyond observability at accelerated rates; future astronomers would see only an isolated local star island.`,
        speculativeObservation: "Intergalactic travel becomes mathematically unreachable; civilizations are perpetually marooned within solitary island galaxies.",
        metricA: {
          label: "Cosmic Horizon",
          val: `-${(val * 1.4).toFixed(0)}%`
        },
        metricB: {
          label: "Dilution Rate",
          val: `+${val}%`
        },
        metricC: {
          label: "Long-term Fate",
          val: "Cold Big Rip"
        }
      };
    }
    return {
      stateLabel: `Negative/Reduced Cosmological Constant (${val}%)`,
      subLabel: "Gravitational deceleration & prospective Big Crunch",
      summary: "The outward repulsive tension of the vacuum weakens or inverts into an attractive stress tensor.",
      physicsConsequence: "Universal expansion slows, halts, and reverses. Galaxy clusters collapse toward a fiery universal singularity.",
      speculativeObservation: "Civilizations face a burning, blueshifted sky as the cosmic microwave background heats up to stellar incinerating temperatures.",
      metricA: {
        label: "Cosmic Deceleration",
        val: `+${Math.abs(val)}%`
      },
      metricB: {
        label: "Ultimate Fate",
        val: "Big Crunch"
      },
      metricC: {
        label: "Singularity Epoch",
        val: "~40 Gyr"
      }
    };
  }
}, {
  id: "electron-mass",
  name: "Electron Mass",
  symbol: "m_e",
  tagline: "Atomic size & stellar degeneracy",
  icon: Sparkles,
  unit: "%",
  sliderLabel: "Electron Rest Mass Variation (Δm_e / m_e0)",
  min: -50,
  max: 50,
  step: 5,
  defaultVal: 0,
  established: "Because the Bohr radius scales inversely with electron mass (a₀ ∝ 1/m_e), a lighter electron inflates atomic volumes (V ∝ m_e⁻³), lowering the density of all condensed matter. If m_e was greater than the neutron-proton mass difference (~1.29 MeV), free protons would decay into neutrons.",
  speculation: "If electrons were featherweight, macroscopic biology would swell to gigantic proportions, with delicate gossamer organisms spanning hundreds of meters without extra mass.",
  visualType: "electron-cloud",
  getReadout: (val: number) => {
    if (val === 0) {
      return {
        stateLabel: "Standard Electron Mass (0.511 MeV/c²)",
        subLabel: "Stable proton-neutron mass hierarchy & solid matter",
        summary: "Protons are lighter than neutrons by 1.29 MeV; neutral hydrogen atoms are completely stable against spontaneous electron capture.",
        physicsConsequence: "White dwarf stars are supported by electron degeneracy pressure up to the Chandrasekhar limit (~1.44 solar masses).",
        speculativeObservation: "Biological cells operate with optimal mechanical rigidity and enzyme molecular scale.",
        metricA: {
          label: "Atomic Volume",
          val: "1.00 V₀"
        },
        metricB: {
          label: "Solid Density",
          val: "1.00 ρ₀"
        },
        metricC: {
          label: "Proton Stability",
          val: "Absolute"
        }
      };
    }
    if (val > 0) {
      const shrinkFactor = (1 / (1 + val / 100)).toFixed(2);
      return {
        stateLabel: `Heavier Electron Mass (+${val}%)`,
        subLabel: "Compressed atomic volumes & risk of neutronization",
        summary: "Atoms shrink into dense, hyper-compact structures; high electron energy triggers spontaneous proton conversion.",
        physicsConsequence: `Atomic sizes shrink by ${(val * 0.9).toFixed(0)}%. Chemical bonds become far shorter, creating ultra-dense crystalline solids.`,
        speculativeObservation: "Biology would be microscopic, ultra-fast, and highly resistant to ionizing radiation due to dense atomic packing.",
        metricA: {
          label: "Bohr Radius",
          val: `${shrinkFactor} a₀`
        },
        metricB: {
          label: "Matter Density",
          val: `+${(val * 2.8).toFixed(0)}%`
        },
        metricC: {
          label: "Degeneracy Limit",
          val: "Suppressed"
        }
      };
    }
    const expandFactor = (1 / Math.max(0.2, 1 + val / 100)).toFixed(2);
    return {
      stateLabel: `Lighter Electron Mass (${val}%)`,
      subLabel: "Expanded atomic orbitals & diffuse, fragile matter",
      summary: "Atoms swell into massive, ethereal electron clouds with very weak intermolecular cohesive bonds.",
      physicsConsequence: `Atomic volumes expand by up to ${((Math.pow(parseFloat(expandFactor), 3) - 1) * 100).toFixed(0)}%. Solids have ultra-low densities and melt at cryogenic temperatures.`,
      speculativeObservation: "Macroscopic objects become vast, porous, cloud-like gossamer forms that drift on gentle cosmic radiation pressures.",
      metricA: {
        label: "Atomic Volume",
        val: `${Math.pow(parseFloat(expandFactor), 3).toFixed(1)} V₀`
      },
      metricB: {
        label: "Melting Point",
        val: "Cryogenic"
      },
      metricC: {
        label: "Matter Rigidity",
        val: "Gossamer"
      }
    };
  }
}];
export default function LawRewriter() {
  const [activeScenarioId, setActiveScenarioId] = useState<string>("gravity");
  const [sliderValues, setSliderValues] = useState<Record<string, number>>({
    gravity: 0,
    "fine-structure": 0,
    "weak-force": 0,
    "spatial-dims": 0,
    "dark-energy": 0,
    "electron-mass": 0
  });
  const prefersReduced = useReducedMotion();
  const currentScenario = SCENARIOS.find(s => s.id === activeScenarioId) || SCENARIOS[0];
  const currentValue = sliderValues[activeScenarioId] ?? 0;
  const readout = currentScenario.getReadout(currentValue);
  const handleSliderChange = (newVal: number[]) => {
    const val = newVal[0];
    setSliderValues(prev => ({
      ...prev,
      [activeScenarioId]: val
    }));
  };
  const handleResetCurrent = () => {
    setSliderValues(prev => ({
      ...prev,
      [activeScenarioId]: currentScenario.defaultVal
    }));
  };

  // Compute visual transform styles from current slider value (-50% to +50%)
  // Normalized value from -1 to +1
  const normVal = currentValue / 50;

  // Visual simulation parameters
  const orbScale = Math.max(0.65, Math.min(1.5, 1 + normVal * 0.35));
  const orbitSpeed = Math.max(2, Math.min(20, 8 - normVal * 5)); // seconds per rev
  const glowIntensity = Math.max(0.3, Math.min(1.0, 0.6 + normVal * 0.4));
  const ringCount = Math.max(1, Math.min(4, Math.round(2 + normVal)));
  return <section id="law-rewriter" data-nav="dark" className="relative w-full max-w-full overflow-hidden py-14 sm:py-24 border-t border-border/40">
      <div className="relative z-10 max-w-[76rem] mx-auto px-4 sm:px-6 lg:px-8 min-w-0">
        {/* Section Header with SectionHeading */}
        <SectionHeading eyebrow="Interactive Thought Experiment" title="Rewrite a Law of Nature" description="Select a foundational cosmic parameter and adjust its magnitude to observe how physical equilibria, atomic stability, and biological prerequisites reorganize." align="center" className="break-words" />

        {/* 6 Selectable Glass Tiles */}
        <Reveal delay={100}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-4 mb-8">
            {SCENARIOS.map(sc => {
            const Icon = sc.icon;
            const isSelected = sc.id === activeScenarioId;
            const scVal = sliderValues[sc.id] ?? 0;
            const isAltered = scVal !== 0;
            return <button key={sc.id} type="button" onClick={() => setActiveScenarioId(sc.id)} aria-pressed={isSelected} className={`group relative text-left p-3 sm:p-4 rounded-xl transition-all duration-200 outline-none flex flex-col justify-between min-h-[110px] sm:min-h-[140px] cursor-pointer min-w-0 ${isSelected ? "ql-glass-strong border-primary/80 shadow-[0_0_24px_var(--glow-primary)] -translate-y-0.5 sm:-translate-y-1" : "ql-glass border-border/60 hover:border-primary/50 hover:bg-accent/20"}`}>
                  {/* Active highlight top hairline */}
                  {isSelected && <div className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent rounded-t-xl" />}

                  <div className="min-w-0 w-full">
                    <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                      <div className={`size-7 sm:size-8 rounded-lg flex items-center justify-center transition-colors shrink-0 ${isSelected ? "bg-primary text-primary-foreground shadow-[0_0_12px_var(--glow-primary)]" : "bg-muted text-primary group-hover:bg-primary/20"}`}>
                        <Icon className="size-3.5 sm:size-4" />
                      </div>
                      <span className="font-mono text-[11px] sm:text-xs font-bold text-muted-foreground group-hover:text-primary transition-colors shrink-0">
                        {sc.symbol}
                      </span>
                    </div>

                    <h3 className="font-serif text-[11px] sm:text-sm font-bold text-foreground leading-snug line-clamp-2 break-words">
                      {sc.name}
                    </h3>
                  </div>

                  <div className="mt-1.5 sm:mt-2 pt-1.5 sm:pt-2 border-t border-border/40 flex items-center justify-between text-[10px] sm:text-[11px] font-mono min-w-0 w-full">
                    <span className="text-muted-foreground truncate mr-1">
                      {isAltered ? <span className="text-primary font-semibold">
                          {scVal > 0 ? `+${scVal}` : scVal}
                          {sc.unit}
                        </span> : "Standard"}
                    </span>
                    {isSelected && <span className="size-1.5 rounded-full bg-primary shadow-[0_0_6px_var(--glow-primary)] shrink-0" />}
                  </div>
                </button>;
          })}
          </div>
        </Reveal>

        {/* Scenario Explorer Main Workbench (Split-Stage: Columns 1-6 Controller & Simulation, Columns 7-12 Telemetry & Physics) */}
        <Reveal delay={200}>
          <div className="ql-glass-strong rounded-2xl p-4 sm:p-8 lg:p-10 border border-primary/30 shadow-2xl relative overflow-hidden min-w-0">
            {/* Top Status Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-5 sm:pb-6 border-b border-border/60 min-w-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="size-9 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center text-primary shrink-0">
                  <Sliders className="size-4" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] sm:text-xs uppercase font-mono tracking-wider text-primary font-semibold">
                      CALIBRATION MATRIX
                    </span>
                    <span className="text-muted-foreground text-xs hidden sm:inline">•</span>
                    <span className="font-mono text-[11px] sm:text-xs text-muted-foreground">
                      PARAMETER: {currentScenario.symbol}
                    </span>
                  </div>
                  <h3 className="font-serif text-base sm:text-xl font-bold text-foreground truncate break-words">
                    {currentScenario.name}
                  </h3>
                </div>
              </div>

              {/* Reset to standard button */}
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                {currentValue !== 0 && <button type="button" onClick={handleResetCurrent} className="ql-chip text-xs hover:text-foreground min-h-[38px] px-3 inline-flex items-center cursor-pointer" title="Reset parameter to Standard Model equilibrium">
                    <RotateCcw className="size-3 text-primary mr-1.5 shrink-0" />
                    <span>Reset to 0{currentScenario.unit}</span>
                  </button>}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/80 border border-border text-xs font-mono">
                  <span className="text-muted-foreground">Current:</span>
                  <span className={`font-bold ${currentValue === 0 ? "text-primary" : currentValue > 0 ? "text-chart-1 font-semibold" : "text-chart-2 font-semibold"}`}>
                    {currentValue > 0 ? `+${currentValue}` : currentValue}
                    {currentScenario.unit}
                  </span>
                </div>
              </div>
            </div>

            {/* Split Workbench Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 pt-6 sm:pt-8 items-start min-w-0">
              {/* Left Column (Cols 1-6): Slider Controller + Dynamic CSS/SVG Visual Canvas */}
              <div className="lg:col-span-6 space-y-5 sm:space-y-6 min-w-0">
                {/* Slider Control Box */}
                <div className="ql-glass p-4 sm:p-5 rounded-xl border border-border/80 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-3 min-w-0">
                    <label htmlFor={`slider-${currentScenario.id}`} className="text-xs font-mono uppercase tracking-wider text-muted-foreground break-words flex-1">
                      {currentScenario.sliderLabel}
                    </label>
                    <span aria-live="polite" className="font-mono text-sm sm:text-base font-bold text-primary shrink-0">
                      {currentValue > 0 ? `+${currentValue}` : currentValue}
                      {currentScenario.unit}
                    </span>
                  </div>

                  {/* Radix Accessible Slider */}
                  <Slider id={`slider-${currentScenario.id}`} value={[currentValue]} min={currentScenario.min} max={currentScenario.max} step={currentScenario.step} onValueChange={handleSliderChange} aria-label={`${currentScenario.name} adjustment slider`} className={`${styles.sliderRoot} py-3`} />

                  {/* Slider Scale Ticks */}
                  <div className="flex justify-between items-center text-[10px] sm:text-xs font-mono text-muted-foreground pt-1.5 gap-2 min-w-0">
                    <span className="truncate">{currentScenario.min}{currentScenario.unit}</span>
                    <button type="button" onClick={handleResetCurrent} className="hover:text-primary transition-colors cursor-pointer underline decoration-dotted shrink-0 min-h-[32px] inline-flex items-center">
                      Standard (0{currentScenario.unit})
                    </button>
                    <span className="truncate text-right">+{currentScenario.max}{currentScenario.unit}</span>
                  </div>
                </div>

                {/* Live CSS/SVG Simulation Portal */}
                <div className="relative rounded-xl border border-primary/25 bg-background/80 p-4 sm:p-6 min-h-[260px] sm:min-h-[320px] flex flex-col items-center justify-center overflow-hidden ql-grid-dots max-w-full">
                  {/* Subtle corner telemetry stamp */}
                  <div className="absolute top-3 left-3 text-[10px] font-mono text-muted-foreground/80 flex items-center gap-1.5 z-20">
                    <span className="size-1.5 rounded-full bg-primary animate-pulse" />
                    <span>SYNTHETIC PROJECTION</span>
                  </div>

                  <div className="absolute top-3 right-3 text-[10px] font-mono text-primary/70 z-20">
                    STATUS: {currentValue === 0 ? "EQUILIBRIUM" : "PERTURBED"}
                  </div>

                  {/* Dynamic Visual Content depending on scenario */}
                  <div className="relative flex items-center justify-center w-full max-w-[260px] aspect-square my-auto overflow-hidden">
                    {currentScenario.visualType === "star-orbit" && <div className="relative flex items-center justify-center size-full">
                        {/* Central Star */}
                        <div className="rounded-full transition-all duration-300 relative z-10" style={{
                      width: `${46 * orbScale}px`,
                      height: `${46 * orbScale}px`,
                      background: currentValue > 15 ? "radial-gradient(circle at 35% 35%, #ffffff 0%, #38bdf8 40%, #0284c7 80%, #082f49 100%)" : currentValue < -15 ? "radial-gradient(circle at 35% 35%, #fff 0%, #fb923c 45%, #b45309 85%, #451a03 100%)" : "radial-gradient(circle at 35% 35%, #ffffff 0%, #06b6d4 35%, #0891b2 75%, #164e63 100%)",
                      boxShadow: `0 0 ${28 * glowIntensity}px ${6 * glowIntensity}px ${currentValue > 15 ? "var(--glow-laser)" : currentValue < -15 ? "rgba(249, 115, 22, 0.45)" : "var(--glow-primary)"}`
                    }} />

                        {/* Orbit Ring 1 (Inner) */}
                        <div className="absolute rounded-full border border-primary/30 pointer-events-none" style={{
                      width: `${Math.min(130, 110 / (1 + normVal * 0.35))}px`,
                      height: `${Math.min(130, 110 / (1 + normVal * 0.35))}px`,
                      animation: prefersReduced ? "none" : `ql-rotate ${orbitSpeed * 0.75}s linear infinite`
                    }}>
                          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 size-2.5 sm:size-3 rounded-full bg-cyan-400" style={{
                        boxShadow: "0 0 8px 1px var(--glow-primary)"
                      }} />
                        </div>

                        {/* Orbit Ring 2 (Outer) */}
                        <div className="absolute rounded-full border border-primary/20 pointer-events-none" style={{
                      width: `${Math.min(195, 175 / (1 + normVal * 0.35))}px`,
                      height: `${Math.min(195, 175 / (1 + normVal * 0.35))}px`,
                      animation: prefersReduced ? "none" : `ql-rotate ${orbitSpeed * 1.5}s linear infinite`
                    }}>
                          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 size-3 sm:size-3.5 rounded-full bg-indigo-400" style={{
                        boxShadow: "0 0 10px 1px var(--glow-laser)"
                      }} />
                        </div>
                      </div>}

                    {currentScenario.visualType === "atom-orbit" && <div className="relative flex items-center justify-center size-full">
                        {/* Atomic Nucleus */}
                        <div className="size-8 sm:size-9 rounded-full bg-gradient-to-tr from-cyan-500 via-primary to-white flex items-center justify-center font-mono text-[9px] font-bold text-background relative z-10" style={{
                      boxShadow: `0 0 20px var(--glow-primary)`
                    }}>
                          +p+n
                        </div>

                        {/* Electron Orbit Shells */}
                        {[0, 60, 120].map((rot, idx) => {
                      const shellSize = Math.max(80, Math.min(190, 150 * (1 - normVal * 0.4)));
                      return <div key={idx} className="absolute rounded-full border pointer-events-none transition-all duration-300" style={{
                        width: `${shellSize}px`,
                        height: `${shellSize * 0.45}px`,
                        borderColor: currentValue > 0 ? "rgba(6, 182, 212, 0.4)" : "rgba(168, 85, 247, 0.4)",
                        transform: `rotate(${rot}deg)`
                      }}>
                              <div className="absolute -top-1 left-1/2 -translate-x-1/2 size-2 rounded-full bg-primary" style={{
                          boxShadow: "0 0 6px var(--glow-primary)",
                          animation: prefersReduced ? "none" : `ql-rotate ${4 - idx}s linear infinite`
                        }} />
                            </div>;
                    })}
                      </div>}

                    {currentScenario.visualType === "field-rings" && <div className="relative flex items-center justify-center size-full">
                        {/* Weak Gauge Boson Cloud */}
                        <div className="rounded-full border border-dashed border-primary/40 flex items-center justify-center transition-all duration-300" style={{
                      width: `${Math.min(160, 120 * (1 + normVal * 0.4))}px`,
                      height: `${Math.min(160, 120 * (1 + normVal * 0.4))}px`,
                      boxShadow: `inset 0 0 24px ${currentValue > 0 ? "var(--glow-primary)" : "var(--glow-violet)"}`,
                      animation: prefersReduced ? "none" : "ql-soft-pulse 3s ease-in-out infinite"
                    }}>
                          <div className="size-8 rounded-full bg-primary/20 border border-primary flex items-center justify-center text-primary font-mono text-xs font-bold">
                            W±/Z
                          </div>
                        </div>

                        {/* Orbiting Neutrino streams */}
                        <div className="absolute w-[80%] max-w-[176px] aspect-square rounded-full border border-primary/20 pointer-events-none" style={{
                      animation: prefersReduced ? "none" : "ql-rotate-reverse 8s linear infinite"
                    }}>
                          <div className="absolute top-2 left-3 size-2 rounded-full bg-cyan-300 shadow-[0_0_8px_var(--glow-primary)]" />
                          <div className="absolute bottom-2 right-3 size-2 rounded-full bg-purple-300 shadow-[0_0_8px_var(--glow-violet)]" />
                        </div>
                      </div>}

                    {currentScenario.visualType === "hyper-cube" && <div className="relative flex items-center justify-center size-full">
                        {/* 3D / 4D Tesseract Simulation SVG */}
                        <svg viewBox="0 0 200 200" className="w-full max-w-[192px] h-auto aspect-square transition-all duration-300" style={{
                      transform: prefersReduced ? "none" : `rotate(${normVal * 30}deg)`
                    }}>
                          {/* Outer Cube */}
                          <rect x="35" y="35" width="130" height="130" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary/60" />
                          {/* Inner Cube (diminishes or expands with D) */}
                          <rect x={currentValue < 0 ? 55 : 65} y={currentValue < 0 ? 55 : 65} width={currentValue < 0 ? 90 : 70} height={currentValue < 0 ? 90 : 70} fill={currentValue > 0 ? "rgba(6, 182, 212, 0.08)" : "none"} stroke="currentColor" strokeWidth="1.5" className={currentValue > 0 ? "text-primary" : "text-border"} />
                          {/* Hyper-lines connecting vertices */}
                          <line x1="35" y1="35" x2={currentValue < 0 ? 55 : 65} y2={currentValue < 0 ? 55 : 65} stroke="currentColor" strokeWidth="1" className="text-primary/40" />
                          <line x1="165" y1="35" x2={currentValue < 0 ? 145 : 135} y2={currentValue < 0 ? 55 : 65} stroke="currentColor" strokeWidth="1" className="text-primary/40" />
                          <line x1="165" y1="165" x2={currentValue < 0 ? 145 : 135} y2={currentValue < 0 ? 145 : 135} stroke="currentColor" strokeWidth="1" className="text-primary/40" />
                          <line x1="35" y1="165" x2={currentValue < 0 ? 55 : 65} y2={currentValue < 0 ? 145 : 135} stroke="currentColor" strokeWidth="1" className="text-primary/40" />
                        </svg>
                      </div>}

                    {currentScenario.visualType === "expansion-dots" && <div className="relative flex items-center justify-center size-full">
                        {/* Grid of expanding galactic clusters */}
                        <div className="relative w-[85%] max-w-[176px] aspect-square border border-dashed border-primary/20 rounded-xl flex items-center justify-center transition-all duration-300" style={{
                      transform: `scale(${Math.max(0.7, Math.min(1.2, 1 + normVal * 0.25))})`
                    }}>
                          <div className="grid grid-cols-3 gap-5 sm:gap-8">
                            {Array.from({
                          length: 9
                        }).map((_, i) => <div key={i} className="size-2 sm:size-2.5 rounded-full bg-primary relative flex items-center justify-center" style={{
                          boxShadow: "0 0 6px var(--glow-primary)",
                          opacity: currentValue > 20 && i % 2 === 1 ? 0.3 : 1
                        }}>
                                <div className="absolute inset-0 rounded-full animate-ping opacity-25 bg-primary" />
                              </div>)}
                          </div>
                        </div>
                      </div>}

                    {currentScenario.visualType === "electron-cloud" && <div className="relative flex items-center justify-center size-full">
                        {/* Central Proton */}
                        <div className="size-6 rounded-full bg-primary flex items-center justify-center text-[8px] font-mono font-bold text-background z-10 shadow-[0_0_12px_var(--glow-primary)]">
                          p+
                        </div>

                        {/* Diffuse Electron probability cloud (scaled with mass) */}
                        <div className="absolute rounded-full border border-primary/30 transition-all duration-300 flex items-center justify-center" style={{
                      width: `${Math.min(180, 130 / (1 + normVal * 0.45))}px`,
                      height: `${Math.min(180, 130 / (1 + normVal * 0.45))}px`,
                      background: "radial-gradient(circle, rgba(6, 182, 212, 0.25) 0%, rgba(6, 182, 212, 0.05) 60%, transparent 80%)",
                      boxShadow: `0 0 24px rgba(6, 182, 212, ${glowIntensity * 0.4})`
                    }}>
                          <div className="size-2 rounded-full bg-cyan-200 shadow-[0_0_8px_white]" />
                        </div>
                      </div>}
                  </div>

                  {/* Visual Caption Bar */}
                  <div className="w-full mt-auto pt-3 border-t border-border/40 flex flex-wrap items-center justify-between gap-2 text-[11px] sm:text-xs text-muted-foreground font-mono">
                    <span className="flex items-center gap-1.5 text-primary truncate max-w-[200px] sm:max-w-none">
                      <Sparkles className="size-3.5 shrink-0" />
                      <span className="truncate">{readout.stateLabel}</span>
                    </span>
                    <span className="shrink-0">Scale: {currentValue > 0 ? `+${currentValue}%` : `${currentValue}%`}</span>
                  </div>
                </div>
              </div>

              {/* Right Column (Cols 7-12): Dynamic Readout & Dual Separated Claims (Established Science vs Speculation) */}
              <div className="lg:col-span-6 space-y-5 sm:space-y-6 min-w-0">
                {/* Telemetry Metrics Grid */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl ql-glass border border-border/70 text-center font-mono min-w-0">
                  <div className="space-y-1 min-w-0">
                    <span className="text-[9px] sm:text-[10px] uppercase text-muted-foreground block truncate">
                      {readout.metricA.label}
                    </span>
                    <span className="text-xs sm:text-base font-bold text-primary block truncate">
                      {readout.metricA.val}
                    </span>
                  </div>
                  <div className="space-y-1 border-x border-border/60 px-1 min-w-0">
                    <span className="text-[9px] sm:text-[10px] uppercase text-muted-foreground block truncate">
                      {readout.metricB.label}
                    </span>
                    <span className="text-xs sm:text-base font-bold text-foreground block truncate">
                      {readout.metricB.val}
                    </span>
                  </div>
                  <div className="space-y-1 min-w-0">
                    <span className="text-[9px] sm:text-[10px] uppercase text-muted-foreground block truncate">
                      {readout.metricC.label}
                    </span>
                    <span className="text-xs sm:text-base font-bold text-primary block truncate">
                      {readout.metricC.val}
                    </span>
                  </div>
                </div>

                {/* State Readout Overview Card */}
                <div className="ql-glass p-4 sm:p-5 rounded-xl border border-primary/20 space-y-2 min-w-0">
                  <div className="text-[11px] sm:text-xs font-mono uppercase text-primary tracking-wider flex items-center gap-1.5 flex-wrap">
                    <CircleCheck className="size-3.5 shrink-0" />
                    <span>State Readout & Theoretical Classification</span>
                  </div>
                  <h4 className="font-serif text-sm sm:text-lg font-bold text-foreground break-words">
                    {readout.subLabel}
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
                    {readout.summary}
                  </p>
                </div>

                {/* Block 1: WHAT PHYSICS TELLS US (Established Science Badge - Cyan) */}
                <div className="ql-glass p-4 sm:p-6 rounded-xl border border-cyan-500/30 bg-cyan-950/10 relative min-w-0">
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-primary/20 text-primary border-primary/40 font-mono text-[10px] sm:text-[11px] uppercase tracking-wider py-0.5 px-2.5">
                        Established Science
                      </Badge>
                      <span className="text-xs font-mono text-muted-foreground hidden sm:inline">
                        Rigorous Astrophysical Constraints
                      </span>
                    </div>
                    <Atom className="size-4 text-primary shrink-0" />
                  </div>

                  <h5 className="font-serif text-xs sm:text-sm font-bold text-foreground mb-1.5 break-words">
                    What Physics Tells Us
                  </h5>
                  <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans mb-3 break-words">
                    {currentScenario.established}
                  </p>
                  <p className="text-[11px] sm:text-xs text-muted-foreground font-mono border-t border-cyan-500/20 pt-2.5 break-words">
                    Perturbation consequence: {readout.physicsConsequence}
                  </p>
                </div>

                {/* Block 2: WHAT WE CAN ONLY IMAGINE (Speculation Badge - Violet/Gold) */}
                <div className="ql-glass p-4 sm:p-6 rounded-xl border border-purple-500/30 bg-purple-950/10 relative min-w-0">
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/40 font-mono text-[10px] sm:text-[11px] uppercase tracking-wider py-0.5 px-2.5">
                        Speculation
                      </Badge>
                      <span className="text-xs font-mono text-muted-foreground hidden sm:inline">
                        Theoretical Thought Experiment
                      </span>
                    </div>
                    <Sparkles className="size-4 text-purple-400 shrink-0" />
                  </div>

                  <h5 className="font-serif text-xs sm:text-sm font-bold text-foreground mb-1.5 break-words">
                    What We Can Only Imagine
                  </h5>
                  <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans mb-3 break-words">
                    {currentScenario.speculation}
                  </p>
                  <p className="text-[11px] sm:text-xs text-purple-300/80 font-mono border-t border-purple-500/20 pt-2.5 break-words">
                    Exotic scenario: {readout.speculativeObservation}
                  </p>
                </div>

                {/* Explicit Thought Experiment Footnote */}
                <div className="flex items-start gap-2.5 p-3 rounded-lg bg-card/60 border border-border/50 text-[11px] sm:text-xs text-muted-foreground font-mono min-w-0">
                  <Info className="size-4 text-primary shrink-0 mt-0.5" />
                  <span className="break-words">
                    Note: These projections are educational thought experiments calculated from standard dimensional analysis, general relativity, and quantum mechanics. Constants in our universe remain empirically immutable.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}